// Positional validation for the `type="date"` variant of Input. Each
// rule decides whether a given character is valid at its slot in
// `yyyy-mm-dd` — receives the char plus the running prefix, so slots
// whose validity depends on a previous slot (like the 2nd digit of
// the month) can branch.
//
// We deliberately stop at "could become a real date" instead of
// "definitely a real date" — Feb 30 is a slot-valid string that's
// not a real day. Callers that filter against a Date catch that at
// the next layer (`new Date('2024-02-30')` rolls or returns NaN).

const DATE_POSITION_RULES = [
    // 0: year 1st digit — only 1 or 2 (covers 1xxx and 2xxx).
    char => char === '1' || char === '2',
    // 1-3: rest of the year — any digit.
    char => /\d/.test(char),
    char => /\d/.test(char),
    char => /\d/.test(char),
    // 4: separator.
    char => char === '-',
    // 5: month 1st digit — 0 or 1.
    char => char === '0' || char === '1',
    // 6: month 2nd digit — depends on slot 5 (01-12).
    (char, prefix) => {
        if (prefix[5] === '0') {
            return /[1-9]/.test(char);
        }
        if (prefix[5] === '1') {
            return /[0-2]/.test(char);
        }
        return false;
    },
    // 7: separator.
    char => char === '-',
    // 8: day 1st digit — 0, 1, 2, or 3 (days 01-31).
    char => '0123'.includes(char),
    // 9: day 2nd digit — depends on slot 8 (01-31).
    (char, prefix) => {
        if (prefix[8] === '0') {
            return /[1-9]/.test(char);
        }
        if (prefix[8] === '1' || prefix[8] === '2') {
            return /\d/.test(char);
        }
        if (prefix[8] === '3') {
            return /[01]/.test(char);
        }
        return false;
    }
];

const DATE_MAX_LENGTH = DATE_POSITION_RULES.length;

// Strip anything that isn't a digit or a dash, and cap at the
// yyyy-mm-dd length. Paste of "abc1234" becomes "1234"; paste of
// "2024-05-15-extra" becomes "2024-05-15".
const filterDateChars = value =>
    value.replace(/[^\d-]/g, '').slice(0, DATE_MAX_LENGTH);

// True when every character of `value` obeys its slot rule. Empty
// strings and partial prefixes of a valid date both return true; only
// chars at wrong slots make it false.
const isValidDatePrefix = value => {
    for (let i = 0; i < value.length; i += 1) {
        const rule = DATE_POSITION_RULES[i];
        if (!rule || !rule(value[i], value)) {
            return false;
        }
    }
    return true;
};

export { DATE_MAX_LENGTH, filterDateChars, isValidDatePrefix };
